'use client';

import type { CreditTier, PlanPeriod } from 'components';
import { useState, useEffect, JSX } from 'react';

interface UpgradeCreditTierSelectorProps {
    creditTiers: CreditTier[];
    selectedPeriod: PlanPeriod;
    currencySymbol: string;
    isDisabled: boolean;
    selectedTier: { planId: string; planCode: string; credits: number } | null;
    onChange: (planCode: string | undefined, credits: number) => void;
}

export default function UpgradeCreditTierSelector({
    creditTiers,
    selectedPeriod,
    currencySymbol,
    isDisabled,
    selectedTier,
    onChange,
}: UpgradeCreditTierSelectorProps): JSX.Element {
    const sortedTiers = [...creditTiers].sort((a, b) => a.credits - b.credits);
    const [localSelected, setLocalSelected] = useState<{ planCode: string; credits: number } | null>(null);

    useEffect(() => {
        if (selectedTier) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setLocalSelected({ planCode: selectedTier.planCode, credits: selectedTier.credits });
        } else {
            setLocalSelected(null);
        }
    }, [selectedTier]);

    return (
        <div className="w-full space-y-4">
            {sortedTiers.map((tier) => {
                // Get the correct plan code based on the selected period
                const activePlanCode = selectedPeriod === 'monthly' ? tier.monthlyPlanCode : tier.annualPlanCode;

                // Skip this tier if it doesn't have a plan code for the selected period
                if (!activePlanCode) {
                    return null;
                }

                const tierPrice = selectedPeriod === 'monthly' ? tier.monthlyPrice : tier.annualPrice;
                const additionalCost = tier.additionalCost;
                const isSelected =
                    localSelected?.planCode === activePlanCode && localSelected?.credits === tier.credits;
                const isOptionDisabled = isDisabled || !tier.isAvailable;

                // Use a unique key combining credits and plan code to avoid duplicates
                const uniqueKey = `${tier.credits}-${activePlanCode}`;

                return (
                    <label
                        key={uniqueKey}
                        htmlFor={`tier-${uniqueKey}`}
                        className={`
                            block bg-white cursor-pointer ring-1 ring-gray-300 px-4 py-6 rounded-md relative
                            ${isSelected ? 'ring-blue-500 ring-2' : ''}
                            ${isOptionDisabled ? 'bg-gray-200 cursor-not-allowed' : ''}
                        `}
                    >
                        <div className="flex justify-start gap-2 items-center">
                            <input
                                type="radio"
                                id={`tier-${uniqueKey}`}
                                name="credit-tier-selection"
                                value={activePlanCode}
                                checked={isSelected}
                                disabled={isOptionDisabled}
                                onChange={() => {
                                    if (!isOptionDisabled) {
                                        setLocalSelected({ planCode: activePlanCode, credits: tier.credits });
                                        onChange(activePlanCode, tier.credits);
                                    }
                                }}
                                className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <div className="flex justify-start items-center ml-2 flex-1">
                                <span className="font-inter font-semibold text-sm leading-[var(--text-sm--line-height)] text-gray-900">
                                    {tier.credits.toLocaleString()} credits/month
                                </span>
                            </div>
                            {additionalCost > 0 && (
                                <span className="font-inter font-semibold text-xs leading-[var(--text-xs--line-height)] text-gray-600 ml-auto">
                                    add {currencySymbol}
                                    {additionalCost}/mo
                                </span>
                            )}
                        </div>
                    </label>
                );
            })}
        </div>
    );
}
